import { useMemo } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen, Printer, ShieldCheck, ClipboardX, History, IdCard, ScrollText,
  Building2, GraduationCap, UserCog, MapPin, Layers, QrCode,
  ChevronRight, FileText, LogIn, LayoutDashboard, CheckCircle2,
  Award, Trophy, BarChart3, ClipboardList, Users2,
} from "lucide-react";

const ROLES = [
  { key: "superadmin", label: "Superadministrador", icon: ShieldCheck, color: "#7C1E36",
    resumen: "Controla todo el sistema: sectores, zonas, planteles, usuarios y reportes." },
  { key: "jefe_sector", label: "Jefe de Sector", icon: Layers, color: "#0F4C81",
    resumen: "Vista de solo lectura sobre los planteles y personal de su sector." },
  { key: "supervisor_zona", label: "Supervisor de Zona", icon: MapPin, color: "#0F4C81",
    resumen: "Vista de solo lectura de los planteles de su zona escolar." },
  { key: "director", label: "Director del plantel", icon: UserCog, color: "#235B4E",
    resumen: "Administra su plantel: grupos, personal, boletas, credenciales, bajas y constancias." },
  { key: "docente", label: "Docente", icon: GraduationCap, color: "#BC955C",
    resumen: "Captura alumnos y calificaciones de sus grupos asignados." },
];

// Secciones visibles por rol (en orden). Todas comparten intro/acceso/roles/verificar/faq.
const ROLE_SECTIONS = {
  superadmin:      ["intro", "acceso", "roles", "curp", "superadmin", "jefe", "consolidado", "mipersonal", "director", "docente", "estadistica", "credenciales", "constancias", "reconocimientos", "diplomas", "bajas", "auditoria", "verificar", "faq"],
  jefe_sector:     ["intro", "acceso", "roles", "curp", "jefe", "consolidado", "mipersonal", "auditoria", "verificar", "faq"],
  supervisor_zona: ["intro", "acceso", "roles", "curp", "jefe", "consolidado", "mipersonal", "auditoria", "verificar", "faq"],
  director:        ["intro", "acceso", "roles", "curp", "director", "estadistica", "credenciales", "constancias", "reconocimientos", "diplomas", "bajas", "auditoria", "verificar", "faq"],
  docente:         ["intro", "acceso", "roles", "curp", "docente", "estadistica", "diplomas", "verificar", "faq"],
};

const SECTION_LABEL = {
  intro: "Introducción",
  acceso: "Ingresar al sistema",
  roles: "Roles y permisos",
  curp: "Captura por CURP y género automático",
  superadmin: "Superadministrador",
  jefe: "Jefe de Sector / Supervisor",
  consolidado: "Reporte 911 Consolidado",
  mipersonal: "Mi Personal",
  director: "Director del plantel",
  docente: "Docente",
  estadistica: "Datos Estadísticos del Grupo",
  credenciales: "Credenciales",
  constancias: "Constancias",
  reconocimientos: "Reconocimientos",
  diplomas: "Diplomas",
  bajas: "Bajas y reintegros",
  auditoria: "Auditoría",
  verificar: "Verificación pública (QR)",
  faq: "Preguntas frecuentes",
};

export default function Manual() {
  const { user } = useAuth();
  const rolActual = useMemo(() => ROLES.find((r) => r.key === user?.role)?.label || "usuario", [user]);
  const visibles = useMemo(() => ROLE_SECTIONS[user?.role] || ROLE_SECTIONS.superadmin, [user]);
  const isVisible = (id) => visibles.includes(id);

  return (
    <AppLayout>
      <div className="max-w-4xl pb-16 manual-root">
        <div className="mb-6 flex items-start justify-between gap-4 print:hidden">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Guía</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Manual de usuario
            </h1>
            <p className="text-sm text-[#545454] mt-1">
              Manual completo del Sistema Censo 911. Está sesionado como <strong>{rolActual}</strong>.
              Use el índice para saltar a cualquier sección o descargue este manual en PDF.
            </p>
          </div>
          <Button onClick={() => window.print()} data-testid="btn-print-manual" className="bg-[#235B4E] hover:bg-[#1D4A40] text-white shrink-0">
            <Printer className="w-4 h-4 mr-1.5" /> Descargar PDF
          </Button>
        </div>

        {/* Portada solo visible al imprimir */}
        <div className="hidden print:block text-center mb-8">
          <div className="text-[10px] uppercase tracking-widest text-[#9F2241]">Sistema Censo 911 · Tamaulipas</div>
          <h1 className="text-4xl font-bold text-[#10312B] mt-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Manual de usuario</h1>
          <div className="text-sm text-[#6B7280] mt-2">Guía completa para Superadministrador, Jefe de Sector, Supervisor de Zona, Director y Docente</div>
          <div className="text-xs text-[#9CA3AF] mt-4">Actualizado el {new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })}</div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded p-4 mb-8 print:mb-6" data-testid="manual-toc">
          <div className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-2">Índice</div>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm list-decimal list-inside text-[#10312B]">
            {visibles.map((id) => (
              <li key={id}><a className="hover:underline" href={`#${id}`}>{SECTION_LABEL[id]}</a></li>
            ))}
          </ol>
        </div>

        <Section id="intro" titulo="Introducción" icono={BookOpen} isVisible={isVisible}>
          <p>
            <strong>Censo 911</strong> es un sistema web para capturar el censo escolar (formato 911) en planteles de preescolar,
            primaria y secundaria. Permite administrar sectores, zonas escolares, planteles, personal, grupos y alumnado,
            generar boletas, credenciales, constancias y llevar auditoría de movimientos y bajas.
          </p>
          <Callout tono="info">
            Los cambios se guardan en tiempo real. Cada sesión queda registrada en el sistema y solo puede haber una sesión activa por usuario.
          </Callout>
        </Section>

        <Section id="acceso" titulo="Ingresar al sistema" icono={LogIn} isVisible={isVisible}>
          <Steps>
            <Step n="1">Abra <code className="bg-[#F3F4F6] px-1 rounded">censo911.xyz</code> en un navegador moderno (Chrome, Edge, Safari o Firefox).</Step>
            <Step n="2">Ingrese el <strong>correo institucional</strong> y la <strong>contraseña</strong> que le proporcionaron. Todo acceso queda registrado.</Step>
            <Step n="3">Presione <em>Entrar</em>. Si sus credenciales son válidas, el sistema le llevará al panel correspondiente a su rol.</Step>
          </Steps>
          <MockLogin />
          <Callout tono="warn">
            Si abre sesión en otro dispositivo, la sesión anterior se cierra automáticamente.
            Cambie su contraseña desde <strong>Mi perfil</strong> la primera vez que ingrese.
          </Callout>
        </Section>

        <Section id="roles" titulo="Roles y permisos" icono={UserCog} isVisible={isVisible}>
          <p>Cada usuario tiene uno de cinco roles. Los permisos determinan lo que puede ver o modificar:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {ROLES.map((r) => (
              <div key={r.key} className="border border-[#E5E7EB] rounded p-3 flex gap-3 items-start" style={{ background: `${r.color}08` }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${r.color}20` }}>
                  <r.icon className="w-4 h-4" style={{ color: r.color }} />
                </div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider" style={{ color: r.color }}>{r.label}</div>
                  <div className="text-[13px] text-[#374151] leading-relaxed mt-0.5">{r.resumen}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section id="curp" titulo="Captura por CURP y género automático" icono={ClipboardList} isVisible={isVisible}>
          <p>
            La CURP es la columna vertebral de la captura de personas en el sistema. Al escribirla, el sistema realiza tres acciones automáticamente:
          </p>
          <Steps>
            <Step n="1"><strong>Autocompleta fecha de nacimiento</strong> a partir de los dígitos 5–10 de la CURP.</Step>
            <Step n="2"><strong>Detecta el género</strong> (H/M) desde la posición 11 y lo aplica al formulario.</Step>
            <Step n="3"><strong>Sugiere el prefijo del RFC</strong> (primeros 10 caracteres) cuando el campo está vacío.</Step>
          </Steps>
          <Subtitulo>Validación en tiempo real</Subtitulo>
          <p>
            Si la CURP tiene un formato inválido (menos de 18 caracteres, letras donde deben ir dígitos, etc.), un mensaje en rojo aparece debajo del campo indicando el patrón esperado: <em>4 letras + 6 dígitos AAMMDD + H/M + 5 letras + 2 alfanuméricos</em>. El registro no se puede guardar hasta corregirla.
          </p>
          <Subtitulo>Prefijos automáticos en firmas y documentos</Subtitulo>
          <p>
            En <strong>Diplomas</strong> y <strong>Reconocimientos</strong>, el sistema usa el género del CURP para escoger automáticamente:
          </p>
          <ul className="list-disc pl-5 text-sm text-[#374151] space-y-1">
            <li>Prefijo <strong>PROFR.</strong> (masculino) o <strong>PROFRA.</strong> (femenino) antes del nombre del director y del docente.</li>
            <li>Cargo <strong>DIRECTOR DE LA ESCUELA</strong> o <strong>DIRECTORA DE LA ESCUELA</strong> en la firma.</li>
            <li>El alumno beneficiario del diploma aparece solo con su nombre.</li>
          </ul>
          <Callout tono="tip">
            Si un usuario aparece con el prefijo o género incorrecto en un documento oficial, verifique que su CURP esté capturada correctamente en <strong>Mi perfil</strong>.
          </Callout>
        </Section>

        <Section id="superadmin" titulo="Superadministrador" icono={ShieldCheck} isVisible={isVisible}>
          <p>El panel del Superadministrador muestra tarjetas con totales del sistema: sectores, zonas, planteles, supervisores y alumnos activos.</p>
          <MockDashboard rol="Superadministrador" cards={[
            { label: "Sectores", val: "0", icon: Layers, tint: "#7C1E36" },
            { label: "Zonas", val: "0", icon: MapPin, tint: "#0F4C81" },
            { label: "Planteles", val: "0", icon: Building2, tint: "#BC955C" },
            { label: "Alumnos", val: "0", icon: GraduationCap, tint: "#154360" },
          ]} />
          <Subtitulo>4.1 Sectores</Subtitulo>
          <Steps>
            <Step n="1">Vaya al menú lateral → <strong>Sectores</strong>.</Step>
            <Step n="2">Presione <em>Nuevo sector</em>, capture nombre y clave, guarde.</Step>
            <Step n="3">Asigne un Jefe de Sector desde el detalle del sector.</Step>
          </Steps>
          <Subtitulo>4.2 Zonas escolares</Subtitulo>
          <Steps>
            <Step n="1">Menú → <strong>Zonas escolares</strong> → <em>Nueva zona</em>.</Step>
            <Step n="2">Asigne la zona a un sector y capture el Supervisor.</Step>
          </Steps>
          <Subtitulo>4.3 Planteles y usuarios</Subtitulo>
          <Steps>
            <Step n="1">Menú → <strong>Planteles</strong> → <em>Nuevo plantel</em>. Capture CCT, nivel, turno, domicilio y datos generales.</Step>
            <Step n="2">Asigne un Director del plantel desde el detalle del plantel.</Step>
            <Step n="3">Menú → <strong>Usuarios</strong> para gestionar altas, cambios de correo y bloqueos.</Step>
          </Steps>
          <Subtitulo>4.4 Reportes 911</Subtitulo>
          <p>Desde <strong>Reportes 911</strong> puede consultar el reporte oficial de cualquier plantel del sistema. Además dispone del modo <strong>Consolidado multi-plantel</strong> que suma todas las escuelas del sistema en un solo formato 911 (ver sección específica más adelante).</p>
        </Section>

        <Section id="jefe" titulo="Jefe de Sector y Supervisor de Zona" icono={Layers} isVisible={isVisible}>
          <p>
            Ambos roles tienen <strong>vista de solo lectura</strong> sobre las escuelas bajo su alcance. No pueden modificar datos del plantel, personal, alumnos o calificaciones.
          </p>
          <Steps>
            <Step n="1">Al iniciar sesión, verá el panel con los planteles bajo su alcance (Supervisor: su zona; Jefe: todas las zonas de su sector).</Step>
            <Step n="2">Ingrese a cualquier plantel para revisar personal, alumnos, grupos, boletas y reportes 911.</Step>
            <Step n="3">Menú <strong>Personal de la Escuela</strong>: consulte el personal de cada plantel de su alcance.</Step>
            <Step n="4">Menú <strong>Mi Personal</strong>: administre a su equipo administrativo/técnico directo (sección propia, ver más abajo).</Step>
            <Step n="5">Menú <strong>Reportes 911</strong> → botón <strong>Consolidado multi-plantel</strong>: genera el formato 911 oficial agregado sobre todas sus escuelas.</Step>
          </Steps>
          <Callout tono="info">
            El listado <strong>Historial de bajas</strong> y <strong>Movimientos</strong> también está disponible en modo lectura para auditar los planteles.
          </Callout>
        </Section>

        <Section id="consolidado" titulo="Reporte 911 Consolidado" icono={BarChart3} isVisible={isVisible}>
          <p>
            El <strong>Consolidado 911</strong> genera el mismo formato oficial del 911 pero con todos los datos SUMADOS de las escuelas del alcance del usuario:
          </p>
          <ul className="list-disc pl-5 text-sm text-[#374151] space-y-1">
            <li><strong>Supervisor de Zona</strong>: todas las escuelas de su zona.</li>
            <li><strong>Jefe de Sector</strong>: todas las escuelas de su sector.</li>
            <li><strong>Superadministrador</strong>: todas las escuelas del sistema.</li>
          </ul>
          <Subtitulo>5.1 Cómo generarlo</Subtitulo>
          <Steps>
            <Step n="1">Menú → <strong>Reportes 911</strong>.</Step>
            <Step n="2">Pulse el botón <strong>Consolidado multi-plantel</strong>.</Step>
            <Step n="3">Elija el <strong>Nivel</strong> (Primaria / Preescolar / Secundaria) y el <strong>Ciclo escolar</strong>.</Step>
            <Step n="4">Presione <em>Actualizar</em>. El reporte 911 completo aparecerá con datos identificados como CONSOLIDADO y el número total de planteles incluidos.</Step>
            <Step n="5">Botón <em>Imprimir / PDF</em> para exportar el reporte completo.</Step>
          </Steps>
          <Callout tono="info">
            Todas las secciones del 911 (I. Existencia, II. Población, III. Becas, IV. Personal, V. Espacios físicos, etc.) se agregan automáticamente sumando los alumnos y personal de todos los planteles del alcance.
          </Callout>
        </Section>

        <Section id="mipersonal" titulo="Mi Personal a cargo" icono={Users2} isVisible={isVisible}>
          <p>
            <strong>Mi Personal</strong> es un módulo independiente del 911. Sirve para que el Supervisor de Zona o el Jefe de Sector lleve el control de su equipo administrativo/técnico directo (secretaria, mesa técnica, asesores, etc.). Estos datos no se mezclan con el reporte 911 de las escuelas.
          </p>
          <Subtitulo>Alta y edición</Subtitulo>
          <Steps>
            <Step n="1">Menú → <strong>Mi Personal</strong>.</Step>
            <Step n="2">Presione <em>Nuevo registro</em>. Capture nombre, apellidos, sexo, función, nivel educativo, CURP, RFC, plaza y datos de contacto.</Step>
            <Step n="3">La CURP se valida en tiempo real: si el formato es incorrecto, aparece un mensaje en rojo indicando el error.</Step>
            <Step n="4">Guarde con el botón <em>Guardar</em>.</Step>
          </Steps>
          <Subtitulo>Exportar a PDF</Subtitulo>
          <p>
            Desde la vista principal, botón <strong>Imprimir / PDF</strong>: se abre un reporte en tamaño <strong>carta horizontal</strong> con todo el personal, ideal para archivo o firma. Incluye responsable, fecha y firma al pie.
          </p>
        </Section>

        <Section id="director" titulo="Director del plantel" icono={UserCog} isVisible={isVisible}>
          <p>El panel del Director concentra las tarjetas de acceso rápido a la matrícula y personal del plantel.</p>
          <MockDashboard rol="Director" cards={[
            { label: "Mis planteles", val: "0", icon: Building2, tint: "#235B4E" },
            { label: "Docentes", val: "0", icon: UserCog, tint: "#BC955C" },
            { label: "Grupos", val: "0", icon: BookOpen, tint: "#0F4C81" },
            { label: "Alumnos", val: "0", icon: GraduationCap, tint: "#154360" },
            { label: "Bajas", val: "0", icon: ClipboardX, tint: "#9F2241" },
          ]} />
          <Subtitulo>6.1 Mi plantel</Subtitulo>
          <p>Datos generales del plantel: CCT, turno, domicilio, correo, teléfono, ciclo actual y escala de boletas (letras o numérica).</p>
          <Subtitulo>6.2 Personal docente / por función / de la Escuela</Subtitulo>
          <Steps>
            <Step n="1"><strong>Personal docente</strong>: registre docentes con CURP, RFC, plaza y datos personales.</Step>
            <Step n="2"><strong>Personal por Función</strong>: capture personal de apoyo (dirección, USAER, intendencia, etc.).</Step>
            <Step n="3"><strong>Personal de la Escuela</strong>: mapa unificado de todo el personal, resalta campos faltantes en rojo.</Step>
          </Steps>
          <Subtitulo>6.3 Grupos y nuevo ciclo</Subtitulo>
          <Steps>
            <Step n="1"><strong>Grupos</strong>: cree los grupos del ciclo actual (grado, letra, ciclo, docente titular).</Step>
            <Step n="2"><strong>Nuevo ciclo escolar</strong>: al inicio de curso, avance grupos, promueva alumnos y arranque el nuevo ciclo.</Step>
          </Steps>
          <Subtitulo>6.4 Reportes 911</Subtitulo>
          <p>Genere el reporte oficial 911 con datos agregados de matrícula, personal y espacios físicos. Puede imprimirlo directamente.</p>
          <Subtitulo>6.5 Datos Estadísticos del Grupo</Subtitulo>
          <p>Desde el detalle de un grupo puede acceder a <strong>Datos Estadísticos</strong>: tabla oficial de conteo por edad × sexo × condición (nuevo ingreso / repetidor). Ver sección específica más adelante.</p>
          <Subtitulo>6.6 Reconocimientos y Diplomas</Subtitulo>
          <p>Como Director puede emitir <strong>Reconocimientos</strong> a los docentes de su plantel y <strong>Diplomas</strong> a alumnos destacados. Ambos documentos usan un diseño elegante burgundy + dorado con curvas y medalla, tamaño carta horizontal. Ver secciones específicas.</p>
        </Section>

        <Section id="docente" titulo="Docente" icono={GraduationCap} isVisible={isVisible}>
          <p>El docente accede solo a sus grupos asignados por el Director.</p>
          <Steps>
            <Step n="1">Al iniciar sesión, verá el listado de sus grupos con conteo de alumnos.</Step>
            <Step n="2">Entre a un grupo para <strong>agregar, editar o eliminar</strong> alumnos.</Step>
            <Step n="3">Capture los datos del formato 911 por alumno: fecha de nacimiento, entidad, discapacidad, procedencia, beca, etc. La CURP se valida y autocompleta fecha y género.</Step>
            <Step n="4">Vaya a <strong>Boletas</strong> para capturar las calificaciones por trimestre y generar el PDF.</Step>
            <Step n="5">Menú <strong>Datos Estadísticos</strong>: tabla oficial de edades por grupo para el 911.</Step>
            <Step n="6">Menú <strong>Diplomas</strong>: emita diplomas de aprovechamiento a sus alumnos con lugar y periodo.</Step>
          </Steps>
          <Callout tono="warn">
            El docente <strong>no puede</strong> dar de baja alumnos ni reasignarlos a otro grupo. Estas acciones son exclusivas del Director.
          </Callout>
        </Section>

        <Section id="estadistica" titulo="Datos Estadísticos del Grupo" icono={BarChart3} isVisible={isVisible}>
          <p>
            Tabla oficial que muestra la distribución de alumnos por <strong>edad × sexo × condición</strong> (nuevo ingreso o repetidor), lista para pegar en el formato 911.
          </p>
          <Subtitulo>Cálculo de la edad</Subtitulo>
          <p>
            La edad se calcula respecto a la <strong>fecha de corte del ciclo</strong> configurada por el Superadmin (por defecto 1 de septiembre). El sistema:
          </p>
          <ul className="list-disc pl-5 text-sm text-[#374151] space-y-1">
            <li>Usa el año inicial del ciclo escolar (ej. ciclo 2026-2027 → corte 01-sep-2026).</li>
            <li>Si un alumno es más joven o mayor que el rango del grado, se ubica en la columna extrema (menor / mayor) para no perderlo del conteo.</li>
            <li>Para <strong>2° de primaria</strong>, se oculta la columna "MENOS DE 6 AÑOS" (todas las edades válidas empiezan en 6).</li>
          </ul>
          <Subtitulo>Formato</Subtitulo>
          <p>Todas las columnas de edad tienen el <strong>mismo ancho</strong> para una lectura estética. Filas: HOMBRES (NVO. INGRESO / REPETIDOR), MUJERES (NVO. INGRESO / REPETIDOR) y SUBTOTAL.</p>
          <Callout tono="tip">
            Acceda al reporte desde <strong>Grupos → detalle del grupo → Datos Estadísticos</strong> o URL directa <code className="bg-[#F3F4F6] px-1 rounded">/grupos/&lt;grupoId&gt;/datos-estadisticos</code>.
          </Callout>
        </Section>

        <Section id="credenciales" titulo="Credenciales" icono={IdCard} isVisible={isVisible}>
          <p>Genere credenciales escolares para alumnos y docentes con foto tamaño credencial (25 × 35 mm), QR y datos oficiales.</p>
          <MockCredencial />
          <Subtitulo>8.1 Alcance</Subtitulo>
          <ul className="list-disc pl-5 text-sm text-[#374151] space-y-1">
            <li><strong>Individual</strong>: una sola credencial (frente y reverso en la misma hoja).</li>
            <li><strong>Por grupo</strong>: 3 credenciales por hoja con frente + reverso al costado.</li>
            <li><strong>Todos</strong>: imprime todos los alumnos o docentes del plantel.</li>
          </ul>
          <Subtitulo>8.2 Modo Color / Blanco y negro</Subtitulo>
          <p>El modo <strong>Blanco y negro</strong> convierte los fondos oscuros en blanco con borde negro. Ideal si va a imprimir en impresora de tinta negra o pegará una foto real.</p>
          <Subtitulo>8.3 Código QR</Subtitulo>
          <p>Cada credencial incluye un QR que abre la página pública <strong>/verificar</strong> con los datos del alumno o docente. Se lee con la cámara nativa del celular.</p>
        </Section>

        <Section id="constancias" titulo="Constancias" icono={ScrollText} isVisible={isVisible}>
          <p>Emita constancias oficiales listas para firma del Director en hoja tamaño carta.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3">
            {["Simple", "Con promedio general", "De baja", "Trámite de beca"].map((t) => (
              <div key={t} className="border border-[#E5E7EB] rounded px-3 py-2 text-center bg-white">
                <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">Tipo</div>
                <div className="text-[13px] font-semibold text-[#10312B]">{t}</div>
              </div>
            ))}
          </div>
          <Subtitulo>9.1 Cómo generar</Subtitulo>
          <Steps>
            <Step n="1">Menú → <strong>Constancias</strong>.</Step>
            <Step n="2">Elija el tipo de constancia con los botones.</Step>
            <Step n="3">Alcance: <em>Un alumno</em> o <em>Todo un grupo</em>.</Step>
            <Step n="4">Seleccione grupo y alumno. Para el tipo <em>De baja</em>, solo se listan alumnos ya dados de baja.</Step>
            <Step n="5">Confirme ciclo, fecha de expedición y ciudad. Presione <em>Generar constancia</em>.</Step>
            <Step n="6">Se abrirá la vista de impresión. Presione <em>Imprimir</em> o guarde como PDF.</Step>
          </Steps>
        </Section>

        <Section id="reconocimientos" titulo="Reconocimientos" icono={Award} isVisible={isVisible}>
          <p>
            El <strong>Director</strong> otorga reconocimientos a los docentes de su plantel por su labor, vocación y compromiso. Diseño elegante burgundy + dorado con curvas decorativas espejadas al lado derecho y medalla ornamental, tamaño carta horizontal.
          </p>
          <Subtitulo>Cómo emitir</Subtitulo>
          <Steps>
            <Step n="1">Menú → <strong>Reconocimientos</strong>.</Step>
            <Step n="2">Seleccione el docente, el grupo y el ciclo escolar.</Step>
            <Step n="3">Confirme fecha de expedición y ciudad.</Step>
            <Step n="4">Presione <em>Generar</em>. El PDF se abre listo para imprimir.</Step>
          </Steps>
          <Callout tono="info">
            El nombre del docente aparece antecedido por <strong>PROFR.</strong> o <strong>PROFRA.</strong> según su CURP. La firma del Director se acompaña del cargo <strong>DIRECTOR</strong> o <strong>DIRECTORA DE LA ESCUELA</strong> también derivado del CURP.
          </Callout>
        </Section>

        <Section id="diplomas" titulo="Diplomas" icono={Trophy} isVisible={isVisible}>
          <p>
            Los <strong>Docentes</strong> (y también el Director) pueden otorgar diplomas a alumnos destacados por lugar (1°, 2°, 3°) y periodo (trimestre). Mismo estilo elegante burgundy + dorado con curvas al lado izquierdo y medalla, tamaño carta horizontal.
          </p>
          <Subtitulo>Cómo emitir</Subtitulo>
          <Steps>
            <Step n="1">Menú → <strong>Diplomas</strong>.</Step>
            <Step n="2">Seleccione grupo y alumno.</Step>
            <Step n="3">Elija el <strong>lugar</strong> (1°, 2°, 3°) y el <strong>periodo</strong> (1er, 2do, 3er).</Step>
            <Step n="4">Confirme fecha y ciudad, y presione <em>Generar</em>.</Step>
          </Steps>
          <Callout tono="info">
            El diploma incluye dos firmas: la del <strong>Director(a)</strong> y la del <strong>Docente</strong>, ambos con su prefijo PROFR./PROFRA. y cargo correcto según el CURP.
          </Callout>
        </Section>

        <Section id="bajas" titulo="Bajas y reintegros" icono={ClipboardX} isVisible={isVisible}>
          <p>Cambie el estatus de un alumno sin borrar sus datos. La información permanece disponible para el 911 del siguiente ciclo.</p>
          <Subtitulo>10.1 Dar de baja</Subtitulo>
          <Steps>
            <Step n="1">Menú → <strong>Bajas</strong>.</Step>
            <Step n="2">Seleccione grado y grupo.</Step>
            <Step n="3">Junto al alumno activo pulse <strong>Dar de baja</strong>.</Step>
            <Step n="4">Elija el motivo (dropdown u "Otro" con texto libre) y marque la casilla de doble confirmación.</Step>
            <Step n="5">Presione <em>Sí, dar de baja</em>.</Step>
          </Steps>
          <Subtitulo>10.2 Reintegrar</Subtitulo>
          <p>En la misma vista, el bloque <strong>Dados de baja</strong> muestra los alumnos inactivos. Pulse <strong>Reintegrar</strong>, confirme el checkbox y el alumno vuelve a estar activo con su historial intacto.</p>
        </Section>

        <Section id="auditoria" titulo="Auditoría" icono={History} isVisible={isVisible}>
          <p>Todo movimiento sobre alumnos queda registrado. Solo Superadmin, Directores, Jefes y Supervisores pueden ver la auditoría.</p>
          <Subtitulo>11.1 Historial de bajas</Subtitulo>
          <p>Filtre por plantel, ciclo escolar y motivo. La búsqueda por nombre o CURP funciona en tiempo real.</p>
          <Subtitulo>11.2 Movimientos de alumnos</Subtitulo>
          <p>Cada evento muestra la fecha, tipo (icono y color), grupos involucrados y el responsable (nombre, correo y rol) que ejecutó la acción.</p>
          <MockAuditItem />
        </Section>

        <Section id="verificar" titulo="Verificación pública (QR)" icono={QrCode} isVisible={isVisible}>
          <p>Cualquier persona puede escanear el QR de una credencial y verá una tarjeta pública con los datos codificados.</p>
          <Callout tono="info">
            La vista pública muestra solo los datos codificados en el QR. No consulta la base de datos ni revela información privada.
          </Callout>
        </Section>

        <Section id="faq" titulo="Preguntas frecuentes" icono={FileText} isVisible={isVisible}>
          <FaqItem q="Olvidé mi contraseña.">
            Solicite al Superadministrador o al Director de su plantel que la reinicie. Al ingresar por primera vez, use <strong>Mi perfil → Cambiar contraseña</strong>.
          </FaqItem>
          <FaqItem q="No puedo dar de baja a un alumno.">
            Es porque su rol es <strong>Docente</strong>. Solo el Director del plantel (o el Superadmin) puede cambiar el estatus a BAJA.
          </FaqItem>
          <FaqItem q="Necesito imprimir en blanco y negro.">
            En Credenciales use el botón <strong>Blanco y negro</strong> arriba a la derecha. Elimina fondos oscuros y sombreados para que la tinta luzca limpia.
          </FaqItem>
          <FaqItem q="¿Cómo se toma la fecha de una constancia de baja?">
            Automáticamente. El sistema usa la fecha registrada cuando se dio de baja al alumno desde la sección <strong>Bajas</strong>. Si aún no hay baja registrada, toma la fecha actual.
          </FaqItem>
          <FaqItem q="¿Puedo reasignar un alumno a otro grupo?">
            Sí. Vaya a la tarjeta <strong>Alumnos</strong> del panel del Director, busque al alumno y pulse <em>Reasignar</em>. El sistema conserva todos los datos y calificaciones.
          </FaqItem>
          <FaqItem q="¿Cómo agrego escuelas al Reporte 911 Consolidado?">
            Automáticamente. El sistema toma todas las escuelas de su alcance (zona para Supervisor, sector para Jefe) que coincidan con el <strong>nivel</strong> seleccionado en el filtro. Si no ve una escuela, verifique que esté correctamente asignada a su zona/sector desde el módulo del Superadmin.
          </FaqItem>
          <FaqItem q="Un diploma muestra 'PROFR.' cuando el docente es mujer.">
            El prefijo se toma de la posición 11 del CURP. Verifique que la CURP del docente esté correctamente capturada; si su fecha de nacimiento y género son femeninos, la posición 11 debe ser <strong>M</strong>. Corrija desde <em>Mi Perfil</em> o desde el módulo de usuarios.
          </FaqItem>
          <FaqItem q="La CURP no me deja guardar.">
            El sistema valida el formato en tiempo real. Verifique que tenga exactamente 18 caracteres con este patrón: 4 letras + 6 dígitos (AAMMDD) + H/M + 5 letras + 2 alfanuméricos. Los caracteres deben ser mayúsculas.
          </FaqItem>
        </Section>

        <div className="mt-10 text-center text-xs text-[#9CA3AF] print:mt-4">
          <div>Sistema Censo 911 · Tamaulipas · Manual de usuario</div>
          <div className="mt-1">Actualizado el {new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })}.</div>
        </div>
      </div>

      <style>{`
        /* Numeración automática de secciones. Cuando una sección se oculta por rol,
           las demás siguen contando en orden sin dejar huecos. */
        .manual-root { counter-reset: manualsec; }
        .manual-section { counter-increment: manualsec; }
        .section-number-label::after { content: " " counter(manualsec); }
        @media print {
          @page { size: Letter portrait; margin: 15mm 18mm; }
          html, body { background: white !important; }
          nav, aside, header, .print\\:hidden { display: none !important; }
          .manual-root { max-width: 100% !important; padding: 0 !important; }
          .manual-section { break-inside: avoid; page-break-inside: avoid; }
          .mock-preview { break-inside: avoid; }
          a { color: #10312B; text-decoration: none; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </AppLayout>
  );
}

function Section({ id, titulo, icono: Icon, isVisible, children }) {
  if (isVisible && !isVisible(id)) return null;
  return (
    <section id={id} className="manual-section mb-8 scroll-mt-16">
      <div className="flex items-center gap-3 mb-3 border-b-2 border-[#10312B] pb-2">
        <div className="w-10 h-10 rounded bg-[#10312B] text-white flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#9F2241] section-number-label">Sección</div>
          <h2 className="text-xl font-semibold text-[#10312B] leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>{titulo}</h2>
        </div>
      </div>
      <div className="text-sm leading-relaxed text-[#374151] space-y-2">{children}</div>
    </section>
  );
}

function Subtitulo({ children }) {
  return <h3 className="text-[13px] font-semibold uppercase tracking-wider text-[#10312B] mt-4 mb-1">{children}</h3>;
}

function Steps({ children }) { return <ol className="space-y-2 mt-2">{children}</ol>; }
function Step({ n, children }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <span className="w-5 h-5 rounded-full bg-[#10312B] text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</span>
      <span className="text-[#374151]">{children}</span>
    </li>
  );
}

function Callout({ tono = "info", children }) {
  const map = {
    info:  { bg: "#F0F9FF", border: "#0EA5E9", label: "Nota" },
    warn:  { bg: "#FEF3F2", border: "#9F2241", label: "Importante" },
    tip:   { bg: "#F0FDF4", border: "#235B4E", label: "Consejo" },
  };
  const m = map[tono] || map.info;
  return (
    <div className="my-3 border-l-4 rounded-r px-3 py-2 text-[13px]" style={{ borderColor: m.border, background: m.bg }}>
      <div className="text-[10px] uppercase tracking-widest font-semibold mb-0.5" style={{ color: m.border }}>{m.label}</div>
      <div className="text-[#374151]">{children}</div>
    </div>
  );
}

function FaqItem({ q, children }) {
  return (
    <div className="border-b border-[#F3F4F6] py-3 last:border-b-0">
      <div className="text-sm font-semibold text-[#10312B]">{q}</div>
      <div className="text-[13px] text-[#374151] mt-1 leading-relaxed">{children}</div>
    </div>
  );
}

// ---------- Previews visuales (mock de UI) ----------
function BrowserFrame({ title = "censo911.xyz", children }) {
  return (
    <div className="mock-preview my-4 border border-[#E5E7EB] rounded-lg overflow-hidden shadow-sm bg-white">
      <div className="bg-[#F3F4F6] px-3 py-1.5 flex items-center gap-1.5 border-b border-[#E5E7EB]">
        <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
        <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
        <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
        <div className="ml-3 text-[10px] text-[#6B7280] font-mono">{title}</div>
      </div>
      {children}
    </div>
  );
}

function MockLogin() {
  return (
    <BrowserFrame title="censo911.xyz/login">
      <div className="grid grid-cols-2">
        <div className="bg-gradient-to-br from-[#10312B] to-[#235B4E] text-white p-4 relative">
          <div className="text-[9px] uppercase tracking-widest opacity-70">Sistema Censo 911</div>
          <div className="text-lg font-bold leading-tight mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>Censo escolar</div>
          <div className="text-sm text-[#BC955C] font-bold leading-tight">preescolar, primaria y secundaria.</div>
          <div className="text-[10px] opacity-70 mt-2 max-w-[80%]">Administre planteles, grupos y alumnado.</div>
        </div>
        <div className="bg-white p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#9F2241]">Acceso</div>
          <div className="text-base font-semibold text-[#10312B] mt-0.5">Iniciar sesión</div>
          <div className="mt-2 space-y-1.5">
            <div>
              <div className="text-[9px] text-[#6B7280]">Correo</div>
              <div className="h-6 border border-[#E5E7EB] rounded px-1.5 text-[10px] leading-6 text-[#9CA3AF]">correo@ejemplo.com</div>
            </div>
            <div>
              <div className="text-[9px] text-[#6B7280]">Contraseña</div>
              <div className="h-6 border border-[#E5E7EB] rounded px-1.5 text-[10px] leading-6 text-[#9CA3AF]">••••••••</div>
            </div>
            <div className="h-6 bg-[#235B4E] rounded text-white text-center text-[10px] leading-6 font-semibold">Entrar</div>
          </div>
        </div>
      </div>
      <PreviewCaption>Pantalla de inicio de sesión con marca institucional a la izquierda y formulario a la derecha.</PreviewCaption>
    </BrowserFrame>
  );
}

function MockDashboard({ rol, cards }) {
  return (
    <BrowserFrame title={`censo911.xyz/admin — ${rol}`}>
      <div className="grid grid-cols-[140px_1fr]">
        <div className="bg-[#FAFAFA] p-2 space-y-1 border-r border-[#E5E7EB] text-[10px]">
          <SidebarItem icon={LayoutDashboard} label="Panel" active />
          <SidebarItem icon={Building2} label="Mi plantel" />
          <SidebarItem icon={GraduationCap} label="Grupos" />
          <SidebarItem icon={FileText} label="Reportes" />
          <SidebarItem icon={IdCard} label="Credenciales" />
          <SidebarItem icon={ScrollText} label="Constancias" />
        </div>
        <div className="p-3">
          <div className="text-[9px] uppercase tracking-widest text-[#9F2241]">{rol.toUpperCase()}</div>
          <div className="text-base font-semibold text-[#10312B] leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Panel del {rol}</div>
          <div className="grid grid-cols-5 gap-2 mt-3">
            {cards.map((c) => (
              <div key={c.label} className="border border-[#E5E7EB] rounded p-1.5 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-[8px] uppercase tracking-widest text-[#6B7280]">{c.label}</div>
                  <c.icon className="w-3 h-3" style={{ color: c.tint }} />
                </div>
                <div className="text-lg font-bold mt-0.5" style={{ color: c.tint, fontFamily: "'Montserrat', sans-serif" }}>{c.val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <PreviewCaption>Menú lateral a la izquierda + tarjetas de resumen con los totales del alcance del usuario.</PreviewCaption>
    </BrowserFrame>
  );
}

function SidebarItem({ icon: Icon, label, active }) {
  return (
    <div className={`flex items-center gap-1 px-1.5 py-1 rounded ${active ? "bg-[#EAECEE] text-[#10312B] font-semibold" : "text-[#6B7280]"}`}>
      <Icon className="w-3 h-3" /> {label}
    </div>
  );
}

function MockCredencial() {
  return (
    <div className="mock-preview my-4 flex flex-col items-center gap-2">
      <div className="flex gap-2">
        {/* Frente */}
        <div className="w-[220px] border border-black rounded overflow-hidden bg-white text-[7pt]" style={{ fontFamily: "Arial, sans-serif" }}>
          <div className="bg-[#10312B] text-white flex items-center gap-1 px-1 py-1">
            <div className="w-4 h-4 bg-white rounded-sm" />
            <div className="min-w-0 flex-1">
              <div className="text-[5.5pt] uppercase tracking-wider opacity-80 truncate">ESC. PRIM. EJEMPLO</div>
              <div className="text-[5.5pt] opacity-70">CCT: 00XPR0000X</div>
            </div>
            <div className="text-right">
              <div className="text-[5pt] opacity-70">CREDENCIAL</div>
              <div className="text-[6pt] font-bold uppercase">Alumno</div>
            </div>
          </div>
          <div className="flex px-1 py-1 gap-1" style={{ height: "80px" }}>
            <div className="w-[38px] h-[54px] bg-[#9F2241] rounded flex items-center justify-center text-white text-[10pt] font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>AP</div>
            <div className="flex-1 pl-1">
              <div className="text-[5pt] text-[#6B7280] uppercase tracking-wider">Nombre</div>
              <div className="text-[6.5pt] font-bold uppercase leading-tight">ALUMNO PRUEBA DEMO</div>
              <div className="text-[5pt] text-[#6B7280] uppercase tracking-wider mt-0.5">Grado y grupo</div>
              <div className="text-[6pt] font-semibold">3° "B"</div>
            </div>
            <div className="w-[44px] h-[44px] bg-[#111827] shrink-0 self-start" />
          </div>
          <div className="border-t border-black flex items-center px-1 justify-between text-[5.5pt]" style={{ height: "16px" }}>
            <div><b>Folio:</b> 00XPR-DEMO01</div>
            <div><b>Vigencia:</b> 31/07/2027</div>
          </div>
        </div>
        {/* Reverso */}
        <div className="w-[220px] border border-black rounded overflow-hidden bg-white text-[6.5pt]" style={{ fontFamily: "Arial, sans-serif" }}>
          <div className="bg-[#10312B] text-white px-1 py-1 text-center">
            <div className="text-[6pt] font-bold uppercase">ESC. PRIM. EJEMPLO</div>
            <div className="text-[5pt] opacity-70">Calle Ejemplo No. 100 · Ciudad Demo, Estado</div>
          </div>
          <div className="p-1.5 flex flex-col justify-between" style={{ height: "108px" }}>
            <div>
              <div className="font-bold text-[6.5pt] mb-0.5">Aviso</div>
              <p className="text-[5.5pt] leading-tight text-[#374151]">Esta credencial identifica al alumno como estudiante activo del plantel emisor durante el ciclo vigente.</p>
            </div>
            <div className="text-center">
              <div className="mx-4 border-t border-black" />
              <div className="text-[6pt] font-bold uppercase mt-0.5">PROFRA. DIRECTORA EJEMPLO</div>
              <div className="text-[5pt] uppercase tracking-widest text-[#6B7280]">Directora del plantel</div>
            </div>
          </div>
        </div>
      </div>
      <PreviewCaption>Frente y reverso de una credencial de alumno con foto tamaño credencial (25×35 mm) y QR de verificación. Datos ilustrativos.</PreviewCaption>
    </div>
  );
}

function MockAuditItem() {
  return (
    <div className="mock-preview border border-[#E5E7EB] rounded p-3 bg-white my-3">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[#0F4C8115]">
          <History className="w-4 h-4 text-[#0F4C81]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-[#0F4C81]">Reasignación</span>
            <span className="text-[10px] text-[#9CA3AF]">·</span>
            <span className="text-[11px] text-[#6B7280]">28 feb 2026, 10:15</span>
          </div>
          <div className="text-sm">
            <span className="font-semibold uppercase text-[#111827]">ALUMNO PRUEBA DEMO</span>
            <span className="ml-2 text-[11px] font-mono text-[#9CA3AF]">XXXX000000HXXXXX00</span>
          </div>
          <div className="text-[12px] text-[#374151] mt-0.5">
            De <strong>3° "A"</strong> a <strong>3° "B"</strong>
          </div>
          <div className="text-[11px] text-[#6B7280] mt-1">
            Responsable: <span className="font-medium text-[#374151]">Directora Ejemplo</span>
            <span className="ml-1 text-[10px] uppercase tracking-wider text-[#9CA3AF]">(director)</span>
          </div>
        </div>
        <CheckCircle2 className="w-4 h-4 text-[#235B4E]" />
      </div>
    </div>
  );
}

function PreviewCaption({ children }) {
  return (
    <div className="text-[11px] text-[#6B7280] px-3 py-1.5 border-t border-[#F3F4F6] bg-[#FAFAFA] flex items-center gap-1">
      <ChevronRight className="w-3 h-3" /> {children}
    </div>
  );
}
